import { useFormikContext } from 'formik'
import { useEffect } from 'react'

import { CheckoutFormData } from '@pages/Checkout/hooks/useInitialFormValues'
import { useSettings } from '@queries/settings'

interface GetSeatArguments {
  layouts: Seat.Data[]
  passengersCount: number
  selected?: Seat.Entry[]
}

const sortSeats = (seats: Seat.Entry[]): Seat.Entry[] =>
  seats.sort((a, b) => {
    if (a.coordinates.x !== b.coordinates.x) return a.coordinates.x - b.coordinates.x

    return a.coordinates.y - b.coordinates.y
  })

const getFreeVacantSeats = (seats: Seat.Entry[]): Seat.Entry[] =>
  seats.filter(seat => Number(seat.price) <= 0 && seat.vacant)

const filterSeatsByDeck = (seats: Seat.Entry[], level: number): Seat.Entry[] =>
  seats.filter(seat => seat.coordinates.z === level)

const getSortedSeats = (seats: Seat.Entry[]): Seat.Entry[] => {
  const LOWER_DECK = 0
  const UPPER_DECK = 1

  return [...sortSeats(filterSeatsByDeck(seats, LOWER_DECK)), ...sortSeats(filterSeatsByDeck(seats, UPPER_DECK))]
}

const filterByFareClass = (layouts: Seat.Data[], fareClass: string): Seat.Data[] =>
  layouts.map(seatData => ({
    ...seatData,
    cars: seatData.cars.map(car => ({
      ...car,
      seats: car.seats.filter(seat => seat.fareClass === fareClass),
    })),
  }))

const filterBySelectedSeats = (layouts: Seat.Data[], selected: Seat.Entry[]): Seat.Data[] => {
  const selectedCodes = selected.map(seat => seat.code)

  return layouts.map(seatData => ({
    ...seatData,
    cars: seatData.cars.map(car => ({
      ...car,
      seats: car.seats.filter(seat => !selectedCodes.includes(seat.code)),
    })),
  }))
}

const buildSeats = (seats: Seat.Entry[]): Seat.BySegment =>
  seats.reduce<Seat.BySegment>((result, seat) => {
    if (seat.segmentIndex === undefined) return result
    if (!result[seat.segmentIndex]) return { ...result, [seat.segmentIndex]: [seat] }

    result[seat.segmentIndex] = [...result[seat.segmentIndex], seat]

    return result
  }, {})

const getPreselectedSeats = ({ layouts, passengersCount, selected = [] }: GetSeatArguments): Seat.BySegment => {
  if (layouts.length === 0) return {}

  let preselected: Seat.Entry[] = selected
  const hasPreselectedSeats = selected.length > 0

  const addSeat = (seat: Seat.Entry, segmentIndex: number, carIndex: number): void => {
    preselected.push({ ...seat, segmentIndex, carIndex })
  }

  const removeLastSeat = (): void => {
    preselected.pop()
  }

  const reset = (): void => {
    preselected = []
  }

  for (const seat of layouts) {
    if (preselected.length === passengersCount) break

    for (const car of seat.cars) {
      const freeSeats = getFreeVacantSeats(car.seats)
      const sortedFreeSeats = getSortedSeats(freeSeats)
      const { index: segmentIndex } = seat.segment
      const { index: carIndex } = car

      if (sortedFreeSeats.length + selected.length < passengersCount) {
        reset()
        continue
      }

      for (const currentSeat of sortedFreeSeats) {
        if (preselected.length === passengersCount) break

        const lastSelectedSeat = preselected.at(-1)
        const hasCompletePair = preselected.length % 2 === 0

        if (!lastSelectedSeat || hasPreselectedSeats) {
          addSeat(currentSeat, segmentIndex, carIndex)
          continue
        }

        const isInSameRow = currentSeat.coordinates.x === lastSelectedSeat.coordinates.x
        const isInNextRow = currentSeat.coordinates.x === lastSelectedSeat.coordinates.x + 1

        const isNextToLastSelected = isInSameRow && currentSeat.coordinates.y === lastSelectedSeat.coordinates.y + 1

        if (isNextToLastSelected) {
          addSeat(currentSeat, segmentIndex, carIndex)
          continue
        }

        if (preselected.length === 1) {
          removeLastSeat()
          addSeat(currentSeat, segmentIndex, carIndex)
          continue
        }

        if (hasCompletePair) {
          if (isInSameRow || isInNextRow) {
            addSeat(currentSeat, segmentIndex, carIndex)
          } else {
            reset()
            addSeat(currentSeat, segmentIndex, carIndex)
          }
          continue
        }

        reset()
        addSeat(currentSeat, segmentIndex, carIndex)
      }
    }
  }

  return preselected.length === 0 || preselected.length === passengersCount
    ? buildSeats(preselected)
    : getPreselectedSeats({
        layouts: filterBySelectedSeats(layouts, preselected),
        passengersCount,
        selected: preselected,
      })
}

const isSeatsEmpty = (seats: Seat.BySegment | undefined): boolean => !seats || Object.values(seats).length === 0
const isLayoutEmpty = (layout: Seat.Data[]): boolean => layout.length === 0

const usePreselectedSeats = (outboundSeats: Seat.Data[], inboundSeats: Seat.Data[]): void => {
  const [{ seatSelection }] = useSettings()
  const { values, setFieldValue } = useFormikContext<CheckoutFormData>()
  const { seats, fareClass, passengers } = values
  const isEnabled = seatSelection.enabledOnCheckout && seatSelection.preselectSeats

  useEffect(() => {
    if (!isEnabled || !isSeatsEmpty(seats?.outbound) || isLayoutEmpty(outboundSeats)) return

    const outbound = getPreselectedSeats({
      layouts: filterByFareClass(outboundSeats, fareClass),
      passengersCount: passengers.length,
    })

    if (Object.values(outbound).length > 0) {
      setFieldValue('seats', { ...seats, outbound })
    }
  }, [fareClass, isEnabled, outboundSeats, passengers.length, seats, setFieldValue])

  useEffect(() => {
    if (!isEnabled || !isSeatsEmpty(seats?.inbound) || isLayoutEmpty(inboundSeats)) return

    const inbound = getPreselectedSeats({
      layouts: filterByFareClass(inboundSeats, fareClass),
      passengersCount: passengers.length,
    })

    if (Object.values(inbound).length > 0) {
      setFieldValue('seats', { ...seats, inbound })
    }
  }, [fareClass, isEnabled, inboundSeats, passengers.length, seats, setFieldValue])
}

export default usePreselectedSeats
